import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { trpc } from '@/lib/trpc';
import { Input } from '@/components/ui/Input';
import { Button } from '@/components/ui/Button';
import { ProductListSkeleton } from '@/components/ui/Skeleton';
import { cn } from '@/lib/utils';
import { Product } from '@/types';
import { haptic } from '@/lib/haptics';
import { categoryIcons, categoryColors, getCategoryIcon, getCategoryColor, BUILT_IN_CATEGORIES } from '@/lib/constants';

export function InventoryPage() {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const [categoryFilter, setCategoryFilter] = useState<string>('');
  const [qualityFilter, setQualityFilter] = useState<string>('');
  const [stockFilter, setStockFilter] = useState<'all' | 'low' | 'out'>('all');

  const { data: products, isLoading } = trpc.product.list.useQuery();

  // Get unique categories from products (combines built-in with any custom)
  const allCategories = useMemo(() => {
    if (!products) return BUILT_IN_CATEGORIES as unknown as string[];
    const productCategories = new Set(products.map(p => p.category));
    return [...new Set([...BUILT_IN_CATEGORIES, ...productCategories])];
  }, [products]);

  // Quality options for Flower/Preroll categories
  const qualityOptions = [
    { value: '', label: 'All Quality' },
    { value: 'PREMIUM', label: '💎 AAA', color: 'bg-gradient-gold text-dark' },
    { value: 'HIGH', label: '🌿 Greendoor', color: 'bg-green-600 text-white' },
  ];

  // Show quality filter for flower-type categories
  const showQualityFilter = categoryFilter === 'FLOWER' || categoryFilter === 'PREROLL';

  // Reset quality filter when switching away from flower categories
  React.useEffect(() => {
    if (!showQualityFilter) {
      setQualityFilter('');
    }
  }, [showQualityFilter]);

  // Filter products
  const filteredProducts = products?.filter((product) => {
    const matchesSearch = product.name.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesCategory = !categoryFilter || product.category === categoryFilter;
    const matchesQuality = !qualityFilter || product.quality === qualityFilter;
    const matchesStock =
      stockFilter === 'all' ? true :
      stockFilter === 'low' ? product.currentStock <= product.minStock && product.currentStock > 0 :
      stockFilter === 'out' ? product.currentStock === 0 : true;
    return matchesSearch && matchesCategory && matchesQuality && matchesStock;
  });

  // Summary stats
  const totalProducts = products?.length || 0;
  const lowStockCount = products?.filter(p => p.currentStock <= p.minStock && p.currentStock > 0).length || 0;
  const outOfStockCount = products?.filter(p => p.currentStock === 0).length || 0;

  // Navigate to product edit page
  const handleEditProduct = (product: Product) => {
    haptic.light();
    navigate(`/inventory/${product.id}`, { state: { product } });
  };

  const handleAddProduct = () => {
    haptic.medium();
    navigate('/inventory/new');
  };

  if (isLoading) {
    return (
      <div className="space-y-4 animate-fadeIn">
        <div className="h-12 bg-dark-lighter rounded-xl animate-pulse" />
        <div className="flex gap-2">
          {[1,2,3].map(i => <div key={i} className="h-10 w-20 bg-dark-lighter rounded-full animate-pulse" />)}
        </div>
        <ProductListSkeleton count={6} />
      </div>
    );
  }

  return (
    <div className="space-y-4 animate-fadeIn">
      {/* Search + Add Button */}
      <div className="flex gap-2">
        <div className="relative flex-1">
          <span className="absolute left-4 top-1/2 -translate-y-1/2 text-xl">🔍</span>
          <Input
            placeholder="Search products..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-12"
          />
        </div>
        <button
          onClick={handleAddProduct}
          className="w-12 h-12 bg-gradient-gold rounded-xl flex items-center justify-center text-2xl text-dark font-bold active:scale-[0.95] transition-transform shadow-lg shadow-primary/20"
        >
          +
        </button>
      </div>

      {/* Stock Filter Pills */}
      <div className="flex gap-2">
        {[
          { value: 'all', label: 'All', count: totalProducts },
          { value: 'low', label: 'Low Stock', count: lowStockCount },
          { value: 'out', label: 'Out', count: outOfStockCount },
        ].map((filter) => (
          <button
            key={filter.value}
            onClick={() => { haptic.select(); setStockFilter(filter.value as 'all' | 'low' | 'out'); }}
            className={cn(
              'px-4 py-2 rounded-full text-sm font-medium transition-all active:scale-[0.97]',
              stockFilter === filter.value
                ? 'bg-gradient-gold text-dark'
                : 'bg-dark-card border border-gray-700 text-gray-400'
            )}
          >
            {filter.label} ({filter.count})
          </button>
        ))}
      </div>

      {/* Category Pills (horizontal scroll) - dynamically generated */}
      <div className="flex gap-2 overflow-x-auto pb-2 hide-scrollbar">
        <button
          onClick={() => { haptic.select(); setCategoryFilter(''); }}
          className={cn(
            'px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-all active:scale-[0.97]',
            !categoryFilter ? 'bg-primary text-dark' : 'bg-dark-card border border-gray-700 text-gray-400'
          )}
        >
          All Categories
        </button>
        {allCategories.map((key) => (
          <button
            key={key}
            onClick={() => { haptic.select(); setCategoryFilter(key); }}
            className={cn(
              'flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-all active:scale-[0.97]',
              categoryFilter === key ? 'bg-primary text-dark' : 'bg-dark-card border border-gray-700 text-gray-400'
            )}
          >
            <span>{getCategoryIcon(key)}</span>
            <span>{key.charAt(0) + key.slice(1).toLowerCase().replace('_', ' ')}</span>
          </button>
        ))}
      </div>

      {/* Quality Filter Pills - Only show for Flower/Preroll */}
      {showQualityFilter && (
        <div className="flex gap-2 overflow-x-auto pb-2 hide-scrollbar">
          {qualityOptions.map((option) => (
            <button
              key={option.value}
              onClick={() => { haptic.select(); setQualityFilter(option.value); }}
              className={cn(
                'px-4 py-2 rounded-full text-sm font-bold whitespace-nowrap transition-all active:scale-[0.97]',
                qualityFilter === option.value
                  ? option.color || 'bg-primary text-dark'
                  : 'bg-dark-card border border-gray-700 text-gray-400'
              )}
            >
              {option.label}
            </button>
          ))}
        </div>
      )}

      {/* Summary Cards */}
      <div className="grid grid-cols-3 gap-3">
        <div className="bg-dark-card rounded-2xl p-4 border-2 border-primary/30">
          <div className="text-xs text-gray-400 uppercase tracking-wide">Total</div>
          <div className="text-3xl font-black text-white">{totalProducts}</div>
        </div>
        <div className="bg-dark-card rounded-2xl p-4 border-2 border-warning/30">
          <div className="text-xs text-gray-400 uppercase tracking-wide">Low</div>
          <div className="text-3xl font-black text-warning">{lowStockCount}</div>
        </div>
        <div className="bg-dark-card rounded-2xl p-4 border-2 border-error/30">
          <div className="text-xs text-gray-400 uppercase tracking-wide">Out</div>
          <div className="text-3xl font-black text-error">{outOfStockCount}</div>
        </div>
      </div>

      {/* Product List */}
      {filteredProducts?.length === 0 ? (
        <div className="text-center py-12">
          <div className="text-6xl mb-4">📦</div>
          <h3 className="text-lg font-medium text-gray-400 mb-2">No products found</h3>
          <p className="text-sm text-gray-500 mb-4">Try adjusting your filters</p>
          <Button onClick={handleAddProduct}>+ Add Product</Button>
        </div>
      ) : (
        <div className="space-y-3">
          {filteredProducts?.map((product, index) => {
            const stockPercent = product.maxStock > 0 ? (product.currentStock / product.maxStock) * 100 : 0;
            const isLow = product.currentStock <= product.minStock && product.currentStock > 0;
            const isOut = product.currentStock === 0;
            const colorClass = getCategoryColor(product.category);
            const icon = getCategoryIcon(product.category);

            return (
              <div
                key={product.id}
                onClick={() => handleEditProduct(product)}
                className={cn(
                  'bg-dark-card rounded-2xl p-4 border-2 transition-all animate-fadeIn cursor-pointer active:scale-[0.98]',
                  isOut ? 'border-error/30' : isLow ? 'border-warning/30' : 'border-gray-700/50 hover:border-primary/50',
                  colorClass
                )}
                style={{ animationDelay: `${index * 30}ms` }}
              >
                <div className="flex items-center gap-3 mb-3">
                  <span className="text-3xl">{icon}</span>
                  <div className="flex-1 min-w-0">
                    <h3 className="font-bold text-white truncate">{product.name}</h3>
                    <p className="text-sm text-gray-400">
                      {product.category.toLowerCase().replace('_', ' ')}
                      {product.quality && <span className="text-primary"> - {product.quality}</span>}
                    </p>
                  </div>
                  <div className="text-right">
                    <div className={cn(
                      'text-2xl font-black tabular-nums',
                      isOut ? 'text-error' : isLow ? 'text-warning' : 'text-success'
                    )}>
                      {product.currentStock.toFixed(1)}
                    </div>
                    <div className="text-xs text-gray-500">{product.unitType}</div>
                  </div>
                </div>

                {/* Stock Bar */}
                <div className="w-full bg-dark-lighter rounded-full h-2 overflow-hidden">
                  <div
                    className={cn(
                      'h-2 rounded-full transition-all',
                      isOut ? 'bg-error' : isLow ? 'bg-warning' : 'bg-gradient-gold'
                    )}
                    style={{ width: `${Math.min(stockPercent, 100)}%` }}
                  />
                </div>
                <div className="flex justify-between mt-1 text-xs text-gray-500">
                  <span>Min: {product.minStock}</span>
                  <span>Max: {product.maxStock}</span>
                </div>
              </div>
            );
          })}
        </div>
      )}

    </div>
  );
}
